<template>
    <div ref="parentRef" class="flex flex-col items-start">
        <div class="flex items-start gap-2 max-w-full flex-row">
            <!-- Assistant avatar (robot) — only when a persona avatar is provided. -->
            <div v-if="avatarIcon"
                :aria-label="assistantName || undefined"
                class="flex-shrink-0 mt-1 flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-indigo-500 text-white shadow-sm">
                <i :class="avatarIcon" class="text-sm" />
            </div>
            <!-- MYAC-1151 — the finished answer has NO background, so `px-1`
                 (not `px-4`): padding nothing costs width in a 420px panel.
                 `BUBBLE_PX` below tracks this padding.
                 Redesign (MYAC-1522) — the answer being written is drawn in the
                 same frame: no bubble, no border, the text arriving where it will
                 stay. Before the first word, a quiet line holds its place: the
                 caret and what the assistant is doing, in the muted colour. -->
            <div class="answer-text rounded-lg px-1 flex-1 min-w-0" :aria-busy="waiting || undefined">
                <Markdown v-if="!waiting || getMessageText(message)" :content="getMessageText(message)" density="compact"
                    :internal-link-handler="internalLinkHandler"
                    :style="{ maxWidth: `${parentWidth}px` }" />
                <!-- Live typing caret while tokens stream in. -->
                <span v-if="message.isPartial" class="typing-caret" aria-hidden="true" />
                <div v-if="waiting" class="answer-waiting flex items-center gap-2 min-w-0 text-surface-500"
                    role="status" data-testid="answer-waiting">
                    <span class="typing-caret shrink-0" aria-hidden="true" />
                    <span class="text-sm truncate min-w-0">{{ currentActivity || currentThinkingMessage }}</span>
                </div>
            </div>
            <Button v-if="debug" :icon="isExpanded ? 'pi pi-eye-slash' : 'pi pi-code'" :text="true" :rounded="true"
                size="small" severity="secondary" @click="toggleRaw"
                v-tooltip.top="isExpanded ? t('chat.hide-raw-message') : t('chat.show-raw-message')" />
        </div>
        <div v-if="isExpanded" class="mt-2 max-w-full w-full">
            <div class="bg-surface-50 border border-surface-300 rounded p-3 text-xs">
                <pre
                    class="whitespace-pre-wrap text-surface-700 overflow-auto max-h-64">{{ formatRawMessage(message) }}</pre>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { type ChatbotMessage, useChatbotStore } from '@/stores/chatbot'
import { activitySpec, translateActivity } from '@/composables/chatActivity'
import Button from 'primevue/button'
import Markdown from '@/components/shared/Markdown.vue'
import { useAppStore } from '@/stores/app'
import { useI18n } from 'vue-i18n'

const appStore = useAppStore()
const chatbotStore = useChatbotStore()
const debug = computed(() => appStore.debug)
const { t, tm } = useI18n()

const props = withDefaults(defineProps<{
    message: ChatbotMessage
    avatarIcon?: string
    assistantName?: string
    /** MYAC-1162 — first refusal on links back into this app; see Markdown.vue. */
    internalLinkHandler?: (path: string) => boolean
}>(), {
    avatarIcon: '',
    assistantName: '',
})

/** Streaming, and no token has arrived to type yet: the quiet waiting line. */
const waiting = computed(() => props.message.isStreaming && !props.message.isPartial)

const isExpanded = ref(false)
const parentRef = ref<HTMLElement | null>(null)
const parentWidth = ref<number>(0)
const selectedThinkingMessage = ref<string>('')

let resizeObserver: ResizeObserver | null = null

/**
 * The answer bubble's own horizontal padding (`px-1`, both sides), subtracted
 * from the observed row width to get the width the markdown may actually use.
 *
 * It caps `maxWidth`, which is what stops a `width: max-content` table (see
 * `.markdown-body table`) from blowing past a flex item that `min-w-0` alone
 * cannot hold. So the number has to track the class on the bubble above: it was
 * 32 for `px-4`, and leaving it at 32 while the padding shrank would have
 * quietly given back the width this card set out to reclaim.
 *
 * Being TOO GENEROUS here is inert; being stingy is not. One frame now draws
 * the finished answer and the one being written (MYAC-1522), so `px-1` is exact
 * for both.
 *
 * The reason it is inert is worth stating precisely, because "max-width" reads
 * like a target and is not one. This element is a block in normal flow, so its
 * USED width is its containing block's — the bubble's content box — and a
 * ceiling above that never raises it. The table underneath is then capped by
 * its own `max-width: 100%` against this element and scrolls inside its
 * `overflow: auto` rather than spilling. Measured on the streaming variant in a
 * 420px row: bubble interior 394px, markdown 394px, table 394px with a 499px
 * scrollWidth, nothing past the bubble's padding — with the ceiling sitting
 * 16px above at 412px, unused. The avatar's 40px has never been subtracted
 * either, for the same reason.
 */
const BUBBLE_PX = 8

onMounted(() => {
    if (parentRef.value) {
        parentWidth.value = parentRef.value.offsetWidth

        resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                parentWidth.value = entry.contentRect.width - BUBBLE_PX
            }
        })

        resizeObserver.observe(parentRef.value)
    }
})

onUnmounted(() => {
    if (resizeObserver) {
        resizeObserver.disconnect()
    }
})

const toggleRaw = () => {
    isExpanded.value = !isExpanded.value
}

/**
 * Extract text content from a message's parts array
 * Filters out parts without text (e.g., function calls)
 */
const getMessageText = (message: ChatbotMessage): string => {
    return message.parts
        .filter(part => part.text !== undefined)
        .map(part => part.text!)
        .join('')
}

/**
 * Check if message has no text content yet
 */
const hasNoContent = computed(() => {
    return props.message.isStreaming && !props.message.isPartial && getMessageText(props.message).length === 0
})

/**
 * Get thinking messages array from i18n
 */
const thinkingMessages = computed(() => {
    try {
        const messages = tm('chat.thinking')
        return Array.isArray(messages) ? (messages as string[]) : []
    } catch {
        return []
    }
})

/**
 * Get current thinking message
 */
const currentThinkingMessage = computed(() => {
    if (!hasNoContent.value) {
        return t('chat.streaming')
    }
    // Persona-aware while reasoning, e.g. "CAPAi is thinking…"
    if (props.assistantName) {
        return t('chat.is-thinking', { name: props.assistantName })
    }
    return selectedThinkingMessage.value || t('chat.thinking-fallback')
})

/**
 * The live action shown in the thinking indicator; null when no tool is running.
 * The label vocabulary is `chatActivity.ts` (MYAC-1382): every tool the chat calls
 * has its own, so a long turn never shows a bare "Working on it…".
 */
const currentActivity = computed<string | null>(() => {
    if (!props.message.isStreaming) return null
    // MYAC-1519 — a project chat's step comes from the server's live draft.
    const live = chatbotStore.liveActivity
    if (live) return translateActivity(t, live)
    const fc = chatbotStore.activeToolCall
    return fc ? translateActivity(t, activitySpec(fc.name, (fc.args as Record<string, unknown>) || {})) : null
})

/**
 * Randomly select a thinking message from the available options
 */
const randomizeThinkingMessage = () => {
    const messages = thinkingMessages.value
    if (messages && messages.length > 0) {
        const randomIndex = Math.floor(Math.random() * messages.length)
        selectedThinkingMessage.value = messages[randomIndex]
    } else {
        selectedThinkingMessage.value = t('chat.thinking-fallback')
    }
}

// Watch for message changes to randomize thinking message
watch(
    () => props.message.id,
    () => {
        if (hasNoContent.value) {
            randomizeThinkingMessage()
        }
    },
    { immediate: true }
)

// Also watch for when streaming starts with no content
watch(
    () => hasNoContent.value,
    (hasNoContentVal) => {
        if (hasNoContentVal) {
            randomizeThinkingMessage()
        }
    }
)

/**
 * Format raw message as JSON for display
 * Shows the raw Event data (including thinking parts) if available, otherwise shows the message
 */
const formatRawMessage = (message: ChatbotMessage): string => {
    // Show raw Event data if available (includes all parts with thinking parts)
    // Otherwise show the filtered message
    return JSON.stringify(message.raw || message, null, 2)
}
</script>

<style scoped>
/* Blinking caret that trails the streamed text — the "typing" feel. */
.typing-caret {
    display: inline-block;
    width: 0.5rem;
    height: 1rem;
    margin-left: 2px;
    vertical-align: text-bottom;
    background: currentColor;
    opacity: 0.7;
    animation: caret-blink 1s steps(1) infinite;
}

@keyframes caret-blink {
    50% { opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
    .typing-caret { animation: none; }
}
</style>
